import Box from '@mui/material/Box';
import React, { useState } from 'react';
import { makeStyles } from 'tss-react/mui';
import {
  getWidthInPercent,
  removeEmptyValues,
  toBarChartValues,
} from './helpers/bar-chart.helpers';
import { ELEMENT_CO2_BAR_HEIGHT } from '@ui/ElementList';
import {
  ISimpleBarChartTooltipData,
  SimpleBarChartTooltip,
} from '@features/projects/charts/SimpleBarChartTooltip';
import { guards } from '@shared/helpers/typeguards.helpers';
import {
  getLifecycleBarStripeBackground,
  getLifecycleBarStripePhase,
} from '@shared/helpers/display/lifecycle-bar-stripe.helpers';

export type ISimpleBarChartValue = {
  value: number;
  key?: string;
  opacity?: number;
  tooltipData?: ISimpleBarChartTooltipData;
};

export type ISimpleBarChartProps = {
  value?: number | ISimpleBarChartValue[] | number[] | Record<string, number>;
  maxValue?: number;
  height?: number | string;
  color?: string;
  backgroundColor?: string;
  interactive?: boolean;
  /**
   * Whether bar width/left/opacity/background-color changes should animate.
   * The default 1s width transition is desirable when the user switches the
   * selected proposal (bars morph between states), but turns any
   * container-query resize into a queued layout animation. Pass `false` in
   * non-interactive contexts (report pages, thumbnails) where the bars
   * should follow resizing instantly.
   */
  animated?: boolean;
};

export const SimpleBarChart: React.FC<ISimpleBarChartProps> = ({
  value = 0,
  maxValue = 1,
  color = '#bbb',
  backgroundColor,
  height = ELEMENT_CO2_BAR_HEIGHT,
  interactive = true,
  animated = true,
}) => {
  const { classes } = useStyles({ animated });
  const [hoveredIndex, setHoveredIndex] = useState<number>(-1);
  let barPartLeftPosition = 0;

  const heightValue = guards.is.number(height)
    ? `${Math.round(height)}px`
    : height;

  return (
    <Box
      className={classes.background}
      style={{ backgroundColor: backgroundColor ?? 'transparent' }}
      sx={{
        height: heightValue,
      }}
    >
      {removeEmptyValues(toBarChartValues(value)).map(
        ({ key, value, tooltipData, opacity = 1 }, index) => {
          const width = getWidthInPercent(value, maxValue);
          if (width <= 0) return null;

          const left = barPartLeftPosition;
          const shouldFade =
            interactive && hoveredIndex >= 0 && hoveredIndex !== index;

          barPartLeftPosition += width;
          const onMouse = interactive
            ? {
                enter: () => setHoveredIndex(index),
                leave: () => setHoveredIndex(-1),
              }
            : {};
          const stripePhase = getLifecycleBarStripePhase(key);
          return (
            <SimpleBarChartTooltip
              key={key ?? `bar-part-${index}`}
              placement="top"
              disableInteractive
              classes={{ tooltipPlacementTop: classes.tooltip }}
              label={tooltipData?.label}
              co2e={tooltipData?.co2e}
              sek={tooltipData?.sek}
              interactive={interactive}
            >
              <Box
                className={classes.stack}
                onMouseEnter={onMouse.enter}
                onMouseLeave={onMouse.leave}
                style={{
                  backgroundColor: color,
                  left: `${left}%`,
                  width: `calc(${width}% - 1px)`,
                  opacity: shouldFade ? 0.25 : opacity,
                }}
              >
                {stripePhase && (
                  <Box
                    sx={{
                      position: 'absolute',
                      inset: 0,
                      backgroundImage:
                        getLifecycleBarStripeBackground(stripePhase),
                      opacity: 0.8,
                      pointerEvents: 'none',
                    }}
                  />
                )}
              </Box>
            </SimpleBarChartTooltip>
          );
        },
      )}
    </Box>
  );
};

const useStyles = makeStyles<{ animated: boolean }>()(
  (_theme, { animated }) => ({
    background: {
      display: 'flex',
      position: 'relative',
      alignContent: 'center',
      justifyContent: 'flex-start',
      width: '100%',
    },
    stack: {
      height: '100%',
      maxWidth: '100%',
      position: 'absolute',
      transition: animated
        ? 'width 1s ease, left 1s ease, opacity 1s ease, background-color 1s ease'
        : 'none',
    },
    tooltip: {
      maxWidth: '100%',
    },
  }),
);
